import { CompanyPicker } from '@/components/features/Companies/components/CompanyPicker';
import type { CreatedPerson } from '@/components/features/People/forms/AddPersonForm';
import CreatePersonModal from '@/components/features/People/forms/CreatePersonModal';
import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import { PersonSelect, type PersonOption } from '@/components/global/PersonSelect';
import InputError from '@/components/layout/InputError';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useSettings } from '@/contexts/SettingsContext';
import { useAbilities } from '@/hooks/use-abilities';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatCurrencyInput, normalizeCurrencyInput } from '@/lib/utils';
import { createLeadWithOwnerSchema } from '@/lib/validations/lead-validation';
import type { People } from '@/types';
import type { CreateLeadFormProps, LeadFormData, LeadPickerCompany } from '@/types/forms/leads';
import { useForm } from '@inertiajs/react';
import { ArrowDown, ArrowRight, ArrowUp, Building2, Euro, Flag, MapPin, UserPlus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

const PRIORITY_ICONS: Record<string, typeof ArrowRight> = {
    low: ArrowDown,
    medium: ArrowRight,
    high: ArrowUp,
};

const invalidFieldClassName = 'border-destructive ring-destructive/20 ring-[3px]';

/**
 * The lead form, for both creating and editing.
 *
 * One component rather than two: the edit sheet used to be its own file with a
 * different field set (no priority, no preliminary value), a different owner
 * picker and a different layout, so the two answered the same question in two
 * ways. `lead` decides the mode; everything else is shared.
 */
export default function CreateLeadForm({ people, users, currentUser, onSuccess, onCancel, lead = null, compact = false }: CreateLeadFormProps) {
    const { legacy } = useSettings();
    const { t } = useTranslations();
    const { can, isAdministrator } = useAbilities();
    const companyRef = useRef<HTMLDivElement>(null);
    const personRef = useRef<HTMLDivElement>(null);

    const isEditing = lead !== null;
    /**
     * Which company a lead belongs to decides who can see it and what it rolls up
     * to, so moving one is an administrator's call — everyone else sees the
     * company as a locked value. LeadController@update applies the same rule.
     */
    const canEditCompany = !isEditing || isAdministrator;

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [selectedCompany, setSelectedCompany] = useState<LeadPickerCompany | null>(() => {
        if (!lead?.company) {
            return null;
        }

        return {
            id: String(lead.company.id),
            name: lead.company.name,
            company_code: lead.company.company_code || '',
            country: lead.company.country || '',
        };
    });
    // Contacts created from this form. They are saved server-side straight away,
    // so they must appear in the picker without reloading (which would wipe the form).
    const [createdPeople, setCreatedPeople] = useState<People[]>([]);

    const requiredMark = <span className="text-destructive">*</span>;

    const resolveCountryFromAddress = (countryCode?: string, countryName?: string): string | null => {
        const normalizedCode = countryCode?.trim().toUpperCase();
        const normalizedName = countryName?.trim().toLowerCase();

        if (Array.isArray(legacy.COUNTRY_OPTIONS)) {
            if (normalizedCode) {
                const byCode = legacy.COUNTRY_OPTIONS.find((option) => String(option.value).toUpperCase() === normalizedCode);
                if (byCode) {
                    return String(byCode.value);
                }
            }

            if (normalizedName) {
                const byName = legacy.COUNTRY_OPTIONS.find((option) => String(option.label).toLowerCase() === normalizedName);
                if (byName) {
                    return String(byName.value);
                }
            }
        }

        return normalizedCode ?? null;
    };

    const handleCompanySelect = (company: LeadPickerCompany | null) => {
        setSelectedCompany(company);
        setData('company_id', company?.id || '');
        // Auto-set country from company if not already set
        if (company?.country && !data.country) {
            setData('country', company.country);
        }
        // Clear selected person if they're not associated with the new company
        if (data.person_id) {
            const selectedPerson = people.find((p) => p.id === data.person_id);
            if (selectedPerson && selectedPerson.company_id !== company?.id) {
                setData('person_id', '');
            }
        }
    };

    const canCreateContacts = can('contacts.create');

    /**
     * The contact is already persisted by the modal, so it only needs to be added
     * to the local list and selected — reloading here would discard the form.
     */
    const handleContactCreated = (person: CreatedPerson) => {
        const created: People = {
            id: person.uuid,
            first_name: person.first_name,
            last_name: person.last_name,
            email: person.email,
            phone: person.phone ?? '',
            position: person.position ?? '',
            accounting: person.accounting,
            company_id: person.company_id ?? undefined,
            created_at: '',
            updated_at: '',
        } as People;

        setCreatedPeople((previous) => [...previous, created]);
        setData('person_id', created.id);
    };

    // Filter people based on selected company
    const filteredPeople = useMemo(() => {
        if (!selectedCompany?.id) {
            return []; // Show empty list if no company is selected
        }
        return [...people, ...createdPeople].filter((person) => person.company_id === selectedCompany.id);
    }, [people, createdPeople, selectedCompany?.id]);

    /** Anyone who can own a lead, in the shape the shared person picker reads. */
    const ownerOptions = useMemo<PersonOption[]>(
        () =>
            (users ?? []).map((user) => ({
                id: user.id,
                first_name: user.first_name,
                last_name: user.last_name,
                avatar: user.avatar ?? null,
                role: user.role ?? null,
            })),
        [users],
    );

    const leadPriorityOptions = useMemo(
        () =>
            (legacy.LEAD_PRIORITY_OPTIONS ?? []).map((option) => ({
                ...option,
                label: t(`settings.lead_priorities.${option.value}`, option.label),
            })),
        [legacy.LEAD_PRIORITY_OPTIONS, t],
    );

    const { data, setData, post, put, processing, errors, reset, transform } = useForm<LeadFormData>({
        name: typeof lead?.name === 'string' ? lead.name : '',
        company_id: lead?.company_id?.toString() ?? '',
        person_id: lead?.person_id?.toString() ?? '',
        lead_source: lead?.lead_source?.toString() ?? '',
        status: lead?.status?.toString() ?? 'new',
        priority: lead?.priority?.toString() ?? 'medium',
        estimated_value: typeof lead?.estimated_value === 'number' ? formatCurrencyInput(String(lead.estimated_value)) : '',
        // show()/edit() ship the raw model, so this arrives as an ISO datetime;
        // index() ships YYYY-MM-DD. Trim either down for the date picker.
        offer_due_date: lead?.offer_due_date ? String(lead.offer_due_date).slice(0, 10) : '',
        notes: typeof lead?.notes === 'string' ? lead.notes : '',
        lead_owner_id: lead?.lead_owner_id?.toString() ?? currentUser.id,
        country: typeof lead?.country === 'string' ? lead.country : '',
        address:
            (typeof lead?.address === 'string' ? lead.address : '') ||
            [lead?.address_street, lead?.address_house_number, lead?.address_city, lead?.address_municipality, lead?.address_postal_code]
                .filter(Boolean)
                .join(', '),
        address_lat: typeof lead?.address_lat === 'number' ? lead.address_lat : null,
        address_lng: typeof lead?.address_lng === 'number' ? lead.address_lng : null,
        address_street: typeof lead?.address_street === 'string' ? lead.address_street : null,
        address_house_number: typeof lead?.address_house_number === 'string' ? lead.address_house_number : null,
        address_postal_code: typeof lead?.address_postal_code === 'string' ? lead.address_postal_code : null,
        address_city: typeof lead?.address_city === 'string' ? lead.address_city : null,
        address_municipality: typeof lead?.address_municipality === 'string' ? lead.address_municipality : null,
        address_country: typeof lead?.address_country === 'string' ? lead.address_country : null,
    });

    /**
     * Translated replacements for the schema's own messages, which are plain English
     * strings shared by every caller. Only fields listed here are localised; the rest
     * fall through to the schema's wording.
     */
    const zodMessages: Record<string, string> = {
        name: t('leads.forms.create.validation.object_name_required', 'Object name is required.'),
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const validationData = {
                ...data,
                name: data.name.trim(),
                company_id: !data.company_id || data.company_id === 'none' ? '' : data.company_id,
                person_id: !data.person_id || data.person_id === 'none' ? '' : data.person_id,
                lead_source: data.lead_source || '',
                lead_owner_id: !data.lead_owner_id || data.lead_owner_id === 'auto' ? '' : data.lead_owner_id,
                estimated_value: normalizeCurrencyInput(data.estimated_value),
            };

            const validationResult = createLeadWithOwnerSchema.safeParse(validationData);

            if (!validationResult.success) {
                const fieldErrors: Record<string, string> = {};
                validationResult.error.issues.forEach((issue) => {
                    if (issue.path.length > 0) {
                        const fieldName = issue.path[0] as string;
                        fieldErrors[fieldName] = zodMessages[fieldName] ?? issue.message;
                    }
                });
                setValidationErrors(fieldErrors);
                toast.error(t('leads.forms.create.validation.resolve_errors', 'Please fill in all required fields.'));
                return;
            }

            setValidationErrors({});

            if (!isEditing) {
                // A new lead always starts at "new"; the status is moved from the
                // lead page afterwards, never chosen here.
                setData('status', 'new');
            }

            // The value field is displayed with thousands spaces; the backend wants a plain number.
            // Empty optional fields go as null rather than '' so they stay null in the database.
            transform((payload) => ({
                ...payload,
                name: payload.name.trim(),
                estimated_value: normalizeCurrencyInput(payload.estimated_value) || null,
                offer_due_date: payload.offer_due_date || null,
                notes: payload.notes || null,
                // A non-administrator cannot move a lead between companies, so the
                // field is never sent from an edit they own.
                ...(isEditing && !canEditCompany ? { company_id: undefined } : {}),
            }));

            const options = {
                onSuccess: () => {
                    if (!isEditing) {
                        reset();
                    }
                    onSuccess?.();
                    // Toast will be shown by useFlashToasts hook from backend flash message
                },
                onError: (errors: Record<string, string>) => {
                    // Only show toast if backend didn't provide a flash error message
                    // Flash message will be shown by useFlashToasts hook
                    console.error('Form submission errors:', errors);
                },
            };

            if (isEditing) {
                put(`/leads/${lead.id}`, options);
            } else {
                post('/leads', options);
            }
        } catch (error) {
            console.error('Validation error:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 pb-6">
            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            1
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                {t('leads.forms.create.sections.lead_details', 'Lead details')}
                            </CardTitle>
                            <CardDescription>
                                {t('leads.forms.create.sections.lead_details_desc', 'Fill in core opportunity information.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* First and required: the object is what everything downstream is
                        named after, so it is answered before the customer. */}
                    <div className="space-y-2">
                        <Label htmlFor="name">
                            {t('leads.forms.create.labels.object_name', 'Object name')} {requiredMark}
                        </Label>
                        <Input
                            id="name"
                            type="text"
                            value={data.name}
                            onChange={(e) => setData('name', e.target.value)}
                            placeholder={t('leads.forms.create.placeholders.enter_object_name', 'Enter object name')}
                            className={cn((errors.name || validationErrors.name) && invalidFieldClassName)}
                        />
                        <p className="text-muted-foreground text-xs">
                            {t(
                                'leads.forms.create.hints.object_name',
                                'Names this lead everywhere: its calculations and, once converted, the project all take this name.',
                            )}
                        </p>
                        <InputError message={errors.name || validationErrors.name} />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="company_id">
                            {t('leads.forms.create.labels.company', 'Company')} {requiredMark}
                        </Label>
                        <div ref={companyRef}>
                            {canEditCompany ? (
                                <CompanyPicker
                                    selectedCompany={selectedCompany}
                                    onCompanySelect={handleCompanySelect}
                                    placeholder={t('leads.forms.create.placeholders.select_company', 'Select company...')}
                                    className={cn((errors.company_id || validationErrors.company_id) && invalidFieldClassName)}
                                />
                            ) : (
                                // Read-only rather than a disabled picker: there is nothing
                                // to open, and the value is what matters here.
                                <div className="border-input bg-muted/40 text-muted-foreground flex h-9 items-center gap-2 rounded-md border px-3 text-sm">
                                    <Building2 className="h-4 w-4 shrink-0" />
                                    <span className="truncate">{selectedCompany?.name ?? t('common.not_specified', 'Not specified')}</span>
                                </div>
                            )}
                        </div>
                        {!canEditCompany && (
                            <p className="text-muted-foreground text-xs">
                                {t('leads.forms.edit.hints.company_locked', 'Only an administrator can move an opportunity to another company.')}
                            </p>
                        )}
                        <InputError message={errors.company_id || validationErrors.company_id} />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="person_id">{t('leads.forms.create.labels.key_contact', 'Main contact')}</Label>
                        <div className="flex items-start gap-2">
                            <div ref={personRef} className="min-w-0 flex-1">
                                <Combobox
                                    options={[
                                        ...filteredPeople.map((person) => ({
                                            value: person.id,
                                            label: `${person.first_name} ${person.last_name}`,
                                        })),
                                    ]}
                                    value={data.person_id}
                                    onValueChange={(value) => setData('person_id', value)}
                                    placeholder={
                                        !selectedCompany?.id
                                            ? t('leads.forms.create.placeholders.select_company_first', 'Select a company first')
                                            : t('leads.forms.create.placeholders.select_contact', 'Select contact...')
                                    }
                                    searchPlaceholder={t('leads.forms.create.placeholders.search_contact', 'Search contact...')}
                                    emptyText={
                                        !selectedCompany?.id
                                            ? t('leads.forms.create.placeholders.select_company_first', 'Select a company first')
                                            : t('leads.forms.create.placeholders.no_contacts', 'No contacts found.')
                                    }
                                    allowClear
                                    disabled={!selectedCompany?.id}
                                />
                            </div>
                            {canCreateContacts && (
                                <CreatePersonModal
                                    companyId={selectedCompany?.id ?? null}
                                    companyName={selectedCompany?.name ?? null}
                                    onCreated={handleContactCreated}
                                    trigger={
                                        <Button type="button" variant="outline" disabled={!selectedCompany?.id} className="shrink-0">
                                            <UserPlus className="mr-2 h-4 w-4" />
                                            {t('leads.forms.create.buttons.add_contact', 'Add contact')}
                                        </Button>
                                    }
                                />
                            )}
                        </div>
                        {/* The dropdown is a dead end for a company with no contacts yet — point at the way out. */}
                        {canCreateContacts && selectedCompany?.id && filteredPeople.length === 0 && (
                            <p className="text-muted-foreground text-xs">
                                {t('leads.forms.create.hints.no_contacts_add', 'This company has no contacts yet.')}
                            </p>
                        )}
                        <InputError message={errors.person_id || validationErrors.person_id} />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="notes">{t('leads.forms.create.labels.description', 'Description')}</Label>
                        <Textarea
                            id="notes"
                            rows={4}
                            value={data.notes}
                            onChange={(e) => setData('notes', e.target.value)}
                            placeholder={t(
                                'leads.forms.create.placeholders.enter_description',
                                'Describe the object, scope of works and key conditions...',
                            )}
                        />
                        <p className="text-muted-foreground text-xs">
                            {t('leads.forms.create.hints.description', 'Free-form notes about the object, scope of works and tender conditions.')}
                        </p>
                        <InputError message={errors.notes || validationErrors.notes} />
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            2
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {t('leads.forms.create.sections.location', 'Location')}
                            </CardTitle>
                            <CardDescription>{t('leads.forms.create.sections.location_desc', 'Where this opportunity is based.')}</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Address comes first: picking one resolves the country below automatically. */}
                    <div className="space-y-2">
                        <Label htmlFor="address">{t('leads.forms.create.labels.address', 'Address')}</Label>
                        <GoogleMapsProvider>
                            <div
                                className={cn(
                                    (errors.address || validationErrors.address) &&
                                        '[&_input]:border-destructive [&_input]:ring-destructive/20 [&_input]:ring-[3px]',
                                )}
                            >
                                <AddressSearchInput
                                    value={data.address ?? ''}
                                    onChange={(value) => {
                                        setData('address', value);
                                        setData('address_street', null);
                                        setData('address_house_number', null);
                                        setData('address_postal_code', null);
                                        setData('address_city', null);
                                        setData('address_municipality', null);
                                        setData('address_country', null);
                                        setData('address_lat', null);
                                        setData('address_lng', null);
                                    }}
                                    onAddressSelect={(address) => {
                                        setData('address', address.label ?? '');
                                        setData('address_street', address.street ?? null);
                                        setData('address_house_number', address.house_number ?? null);
                                        setData('address_postal_code', address.postal_code ?? null);
                                        setData('address_city', address.city ?? null);
                                        setData('address_municipality', address.city ?? null);
                                        setData('address_country', address.country_code ?? address.country ?? null);
                                        setData('address_lat', address.lat ?? null);
                                        setData('address_lng', address.lng ?? null);

                                        const resolvedCountry = resolveCountryFromAddress(address.country_code, address.country);
                                        if (resolvedCountry) {
                                            setData('country', resolvedCountry);
                                        }
                                    }}
                                    placeholder={t('leads.forms.create.placeholders.enter_address', 'Enter address...')}
                                    className="w-full"
                                />
                            </div>
                        </GoogleMapsProvider>
                        <InputError message={errors.address || validationErrors.address} />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="country">{t('leads.forms.create.labels.country', 'Country')}</Label>
                        <Combobox
                            options={legacy.COUNTRY_OPTIONS || []}
                            value={data.country}
                            onValueChange={(value) => setData('country', value)}
                            placeholder={t('leads.forms.create.placeholders.select_country', 'Select country...')}
                            searchPlaceholder={t('leads.forms.create.placeholders.search_country', 'Search country...')}
                            emptyText={t('leads.forms.create.placeholders.no_countries', 'No countries found.')}
                            allowClear
                        />
                        {/* Editable on purpose: Google cannot geocode every rural or hand-typed address. */}
                        <p className="text-muted-foreground text-xs">
                            {t('leads.forms.create.hints.country_autofilled', 'The country is filled in automatically from the selected address.')}
                        </p>
                        <InputError message={errors.country || validationErrors.country} />
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            3
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <Flag className="h-4 w-4" />
                                {t('leads.forms.create.sections.classification', 'Classification & ownership')}
                            </CardTitle>
                            <CardDescription>
                                {t('leads.forms.create.sections.classification_desc', 'Set the priority and assign an owner.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="priority">
                            {t('leads.forms.create.labels.priority', 'Priority')} {requiredMark}
                        </Label>
                        <div id="priority" className="grid grid-cols-3 gap-2">
                            {leadPriorityOptions.map((option) => {
                                const isActive = data.priority === option.value;
                                const Icon = PRIORITY_ICONS[option.value] ?? ArrowRight;

                                return (
                                    <Button
                                        key={option.value}
                                        type="button"
                                        variant="outline"
                                        className={cn(
                                            'h-11 justify-center gap-2 rounded-sm',
                                            isActive
                                                ? 'border-primary bg-primary/15 text-primary ring-primary/50 hover:bg-primary/20 dark:bg-primary/20 ring-1'
                                                : 'hover:bg-muted/60',
                                        )}
                                        onClick={() => setData('priority', option.value)}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {option.label}
                                    </Button>
                                );
                            })}
                        </div>
                        <InputError message={errors.priority || validationErrors.priority} />
                    </div>

                    <div className="grid items-start gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="estimated_value">{t('leads.forms.create.labels.estimated_value', 'Preliminary value')}</Label>
                            <div className="relative">
                                <Euro className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="estimated_value"
                                    type="text"
                                    inputMode="decimal"
                                    className={cn('pl-9', (errors.estimated_value || validationErrors.estimated_value) && invalidFieldClassName)}
                                    value={data.estimated_value}
                                    onChange={(e) => setData('estimated_value', formatCurrencyInput(e.target.value))}
                                    placeholder={t('leads.forms.create.placeholders.enter_estimated_value', '0.00')}
                                />
                            </div>
                            <p className="text-muted-foreground text-xs">
                                {t(
                                    'leads.forms.create.hints.estimated_value',
                                    'A rough budget estimate. You can refine it later when preparing the offer.',
                                )}
                            </p>
                            <InputError message={errors.estimated_value || validationErrors.estimated_value} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="offer_due_date">{t('leads.forms.create.labels.offer_due_date', 'Offer deadline')}</Label>
                            <DatePicker
                                value={data.offer_due_date}
                                onValueChange={(value) => setData('offer_due_date', value)}
                                placeholder={t('leads.forms.create.placeholders.select_offer_due_date', 'Select date...')}
                            />
                            <p className="text-muted-foreground text-xs">
                                {t('leads.forms.create.hints.offer_due_date', 'The date by which the offer must be submitted to the customer.')}
                            </p>
                            <InputError message={errors.offer_due_date || validationErrors.offer_due_date} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="lead_owner_id">{t('leads.forms.create.labels.lead_owner', 'Owner')}</Label>
                        <PersonSelect
                            people={ownerOptions}
                            value={data.lead_owner_id || null}
                            onValueChange={(value) => setData('lead_owner_id', value ?? '')}
                            variant="field"
                            showRole
                            allowClear={false}
                            // Inside a sheet the rest of the page is inert, which would
                            // otherwise swallow clicks on the portalled list.
                            modal={compact}
                            labels={{ placeholder: t('leads.forms.create.placeholders.select_lead_owner', 'Select owner...') }}
                            className={cn((errors.lead_owner_id || validationErrors.lead_owner_id) && invalidFieldClassName)}
                        />
                        <InputError message={errors.lead_owner_id || validationErrors.lead_owner_id} />
                    </div>
                </CardContent>
            </Card>

            <div className="bg-background/95 sticky bottom-0 z-10 flex justify-between gap-2 rounded-md border p-4 backdrop-blur">
                {/* In a sheet there is nothing to go back to — the caller closes it. */}
                <Button type="button" variant="outline" onClick={() => (onCancel ? onCancel() : window.history.back())}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={processing}>
                    {processing
                        ? t('leads.forms.create.buttons.saving', 'Saving...')
                        : isEditing
                          ? t('leads.forms.edit.buttons.update_lead', 'Update Lead')
                          : t('leads.forms.create.buttons.create_lead', 'Create Opportunity')}
                </Button>
            </div>
        </form>
    );
}
